import { formatMoney } from './formatMoney';

/**
 * ONE source of truth for what the client portal SAYS about a booking's money.
 *
 * ROOT CAUSE this exists (Jun 2026): every status sentence on the payments tab
 * branched on "is anything outstanding?" and, when something was, assumed the
 * outstanding thing was the RETAINER and that the date was therefore at risk.
 * A booking with the retainer paid, the contract balance paid, and a $1.00
 * extra charge added afterwards was told: "You're 2 steps away from locking in
 * your date", "Next step: Pay your retainer to lock in your date", "Your date
 * is not locked in until payment is received", "Your date is currently being
 * held but not guaranteed", "Complete your remaining balance to lock it in
 * permanently" — five statements, all false for that client.
 *
 * The question is never "is anything outstanding" but WHICH rows are.
 *
 * `unknown` is a real answer. With no rows to read this says nothing
 * reassuring rather than guessing — silence beats a sentence that might be
 * false on the screen a client pays from.
 */

// The contract itself. Anything else — invoices, add-ons, extra charges — is
// billed on top of it and says NOTHING about whether the date is secured.
export const CONTRACT_TYPES = ['retainer', 'deposit', 'balance'];

const isSettled = (p) => p?.status === 'paid' || p?.status === 'confirmed';
const num = (v) => Number(v) || 0;

export function paymentStatusFrom(payments, totals = {}) {
  const rows = Array.isArray(payments) ? payments : [];
  const paid = num(totals.totalPaid);
  const due = num(totals.totalDue);
  const progress = { paid, due, label: 'Total paid' };

  if (rows.length === 0) {
    return {
      state: 'unknown', retainerSettled: false, contractSettled: false,
      contractOutstanding: 0, extrasOutstanding: 0, extrasCount: 0,
      progress,
      badge: null, headline: null, subtext: null, urgency: null,
      nextStepCopy: null,
    };
  }

  const contractRows = rows.filter((p) => CONTRACT_TYPES.includes(p.type));
  const extraRows = rows.filter((p) => !CONTRACT_TYPES.includes(p.type));
  const retainerRow = rows.find((p) => p.type === 'retainer' || p.type === 'deposit');
  const retainerTarget = num(retainerRow?.amount);

  // Aggregate, exactly as the rest of the portal reads it: money landing on
  // the wrong line still secures the date.
  const retainerSettled = retainerTarget > 0
    ? paid >= retainerTarget - 0.005
    : contractRows.every(isSettled);
  const contractOutstanding = contractRows
    .filter((p) => !isSettled(p))
    .reduce((s, p) => s + num(p.amount), 0);
  const extrasUnsettled = extraRows.filter((p) => !isSettled(p));
  const extrasOutstanding = extrasUnsettled.reduce((s, p) => s + num(p.amount), 0);
  const contractSettled = retainerSettled && contractOutstanding <= 0.005;

  let state;
  if (!retainerSettled) state = 'retainer_unpaid';
  else if (contractOutstanding > 0.005) state = 'balance_outstanding';
  else if (extrasOutstanding > 0.005) state = 'extras_only';
  else state = 'paid';

  const many = extrasUnsettled.length > 1;
  const COPY = {
    retainer_unpaid: {
      badge: 'Pending Payment',
      headline: 'Secure your date — complete your booking',
      subtext: 'Pay your retainer to lock in your date.',
      urgency: null,
      nextStepCopy: 'Next step: Pay your retainer to lock in your date',
    },
    balance_outstanding: {
      badge: 'Retainer Paid',
      headline: 'Your date is secured — your balance is still to come',
      subtext: `Your retainer is paid and your date is held. ${formatMoney(contractOutstanding)} of your contract remains.`,
      urgency: null,
      nextStepCopy: 'Next step: Settle your remaining contract balance',
    },
    extras_only: {
      badge: 'Contract Settled',
      headline: 'Your date is secured and your contract is settled',
      subtext: `Your retainer and contract balance are both paid. ${
        many ? `${extrasUnsettled.length} additional charges are` : 'One additional charge is'
      } outstanding — ${formatMoney(extrasOutstanding)}, billed on top of your contract.`,
      urgency: null,
      nextStepCopy: many
        ? 'Next step: Settle the additional charges'
        : 'Next step: Settle the additional charge',
    },
    paid: {
      badge: 'Paid in Full',
      headline: 'Your booking is secured — see you at the event',
      subtext: 'Thank you. Every payment has been received and your date is fully confirmed.',
      urgency: null,
      nextStepCopy: null,
    },
  }[state];

  // Full-payment override (single determination, passed in from the portal):
  // when this payment covers the whole balance with nothing owed after, the
  // "retainer" wording in the unpaid state reads as a full payment instead.
  // Every other state keeps its retainer / balance / extras wording.
  const copy = (totals.isFullPayment && state === 'retainer_unpaid')
    ? {
        ...COPY,
        subtext: 'Pay in full to lock in your date.',
        nextStepCopy: 'Next step: Pay in full to lock in your date',
      }
    : COPY;

  return {
    state,
    retainerSettled,
    contractSettled,
    contractOutstanding: Math.round(contractOutstanding * 100) / 100,
    extrasOutstanding: Math.round(extrasOutstanding * 100) / 100,
    extrasCount: extrasUnsettled.length,
    progress,
    ...copy,
  };
}

export const isExtraCharge = (payment) => !CONTRACT_TYPES.includes(payment?.type);

export default paymentStatusFrom;
